import { useState, useEffect, useRef } from 'react'
import { authedFetch } from '../utils/shopifyFetch'

// Module-level cache persists across route changes (component unmount/remount)
const cache = new Map()

/**
 * Stale-while-revalidate hook for page data.
 *
 * - First visit:        loading=true until fetch completes
 * - Return visit:       shows cached data instantly (loading=false),
 *                       silently refetches in background (refreshing=true)
 * - Filter/param change: shows last data while new fetch runs (no blank flash)
 */
export function usePageData(url, { skip = false } = {}) {
  const [data,       setData]       = useState(() => cache.get(url) ?? null)
  const [loading,    setLoading]    = useState(!cache.has(url))
  const [refreshing, setRefreshing] = useState(false)
  const [tick,       setTick]       = useState(0)
  const abortRef = useRef(null)
  const urlRef   = useRef(url)
  urlRef.current = url

  useEffect(() => {
    if (skip || !url) {
      setLoading(false)
      return
    }

    // Cancel any in-flight request for a previous URL
    if (abortRef.current) abortRef.current.abort()
    const controller = new AbortController()
    abortRef.current = controller

    const cached = cache.get(url)
    if (cached !== undefined) {
      setData(cached)
      setLoading(false)
      setRefreshing(true)
    } else {
      setLoading(true)
    }

    // authedFetch, not a bare fetch: App Bridge attaches the session token, and
    // a 401 is retried once with a fresh one. This used to await a token before
    // it would fetch at all, so a stalled App Bridge handshake held the whole
    // page blank — which is what the ten-to-twenty-second navigations were.
    authedFetch(url, { signal: controller.signal })
      .then(r => r.json())
      .then(d => {
        if (urlRef.current === url) {
          cache.set(url, d)
          setData(d)
        }
      })
      .catch(e => { if (e.name !== 'AbortError') console.warn('[usePageData]', e.message) })
      .finally(() => {
        if (urlRef.current === url) {
          setLoading(false)
          setRefreshing(false)
        }
      })

    return () => controller.abort()
  }, [url, skip, tick]) // eslint-disable-line

  function invalidate() { cache.delete(url); setTick(t => t + 1) }

  return { data, loading, refreshing, invalidate }
}

/** Invalidate a cached URL so next mount re-fetches fresh */
export function invalidateCache(url) { cache.delete(url) }
